import { DocumentCategory, DocumentSelectionGroup, KampfmittelauskunftProductId, ProductKind, ProductType } from 'constants/product'
import { Fragment, useCallback, useMemo } from 'react'
import { GRAY_700, GRAY_900 } from 'constants/styling/theme'
import { GermanyRegionCodes, RestrictedRegions } from 'constants/country'
import { MeasurementInfoCard, measurementInfoCardIconSx } from 'components/common/MeasurementInfoCard'
import { MeasurementReviewInfo } from 'components/common/MeasurementReviewInfo'
import { Trans, useTranslation } from 'react-i18next'
import { logAnalyticsEvent, standardDebouncedLogAnalyticsEvent } from 'utils/analytics'
import { productIdsDisplacedBySelectionGroup, usePurchaseFlowProducts } from '../../_main/contexts/PurchaseFlowProducts.context'

import { CatalogueOptionProduct } from 'dataQueries/purchase.query'
import { Collapse } from '@mui/material'
import DynamicQueryContent from 'components/common/DynamicQueryContent/DynamicQueryContent'
import { MUICheckCard } from 'components/common/MUICards/MUICheckCard'
import { MUIIntegerCard } from 'components/common/MUICards'
import { Nullable } from 'models/helpers'
import { ProductCardContent } from '../ProductCardContent/ProductCardContent'
import { ProductCardHeader } from '../ProductCardHeader/ProductCardHeader.component'
import { ProductListHeader } from '../ProductListHeader'
import Stack from '@mui/material/Stack'
import { SystemMessage } from 'components/common/SystemMessage'
import { TransitionGroup } from 'react-transition-group'
import Typography from '@mui/material/Typography'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import { isIntegerProduct } from 'utils/product'
import { repriceAwareQuery } from './repriceAwareQuery'
import { sortProductsKeepingSelectionGroupsAdjacent } from './sortProductsKeepingSelectionGroupsAdjacent'
import { standardTimeoutConfig } from 'utils/animations'
import { usePurchaseFlowConfig } from '../../_main/contexts/PurchaseFlowConfig.context'

/**
 * @interface Props Input properties
 */
interface Props {
  /** Whether to display type heading element */
  displayTypeHeading?: boolean
  /** Whether to display only selected types, products and options */
  onlySelected?: boolean
  /** Whether to disabled selecting/deselecting */
  disabledSelection?: boolean
  /** Whether the product listing is disabled and grayed out */
  disabled?: boolean
}

/**
 * @component Component which lists all products in purchase flow
 * @example
 * <ProductListing />
 */
export const ProductListing: React.FC<Props> = ({
  displayTypeHeading = true,
  onlySelected = false,
  disabledSelection = false,
  disabled = false,
}) => {
  const { t } = useTranslation(['order', 'product_type', 'product_type_description', 'segment', 'product_kind_description', 'step_product', 'step_validation'])

  const {
    getProductCatalogue,
    availableProductTypesPerSegment,
    selectedCategory,
    catalogueCurrency,
    catalogueDiscount,
    regionCode,
    isRepricing,
  } = usePurchaseFlowConfig()
  const {
    selectedFilterTypes,
    selectedSegment,
    selectedProducts,
    selectedProductOptions,
    selectProduct,
    selectOptionProduct,
    unselectOptionProduct,
    unselectProduct,
  } = usePurchaseFlowProducts()

  const handleLogOptionSelection = useCallback((quantity: number, productKind: ProductKind, parentProductKind: ProductKind) => {
    standardDebouncedLogAnalyticsEvent('option_kind_selected', { quantity, productKind, parentProductKind })
  }, [])

  // documentSelectionGroup makes the bewilu-migration rollout segmentable in analytics — the FE
  // has no flag of its own, so this field is the only browser-side signal of which behaviour ran
  const handleLogProductSelection = useCallback((quantity: number, productKind: ProductKind, productType: ProductType, documentSelectionGroup?: Nullable<DocumentSelectionGroup>) => {
    standardDebouncedLogAnalyticsEvent('product_selected', { quantity, productKind, productType, documentSelectionGroup: documentSelectionGroup ?? null })
  }, [])

  const isSelectedRegionRestricted = useMemo(() => {
    return RestrictedRegions.has(regionCode as GermanyRegionCodes)
  }, [regionCode])

  const productTypesToRender = useMemo(() => {
    if (!selectedSegment) return []

    const toRender = [
      ...(availableProductTypesPerSegment[selectedSegment]?.nonBundle || []),
      ...(availableProductTypesPerSegment[selectedSegment]?.bundle || [])
    ].filter(({ productType }) => selectedFilterTypes.has(productType))

    return toRender
  }, [availableProductTypesPerSegment, selectedFilterTypes, selectedSegment])


  // During an embed live reprice the listing keeps rendering from the snapshot catalogue, so the
  // wrapper must not flash its spinner nor unmount the cards while the mutation re-fires.
  const listingQuery = useMemo(() => repriceAwareQuery(getProductCatalogue, isRepricing), [getProductCatalogue, isRepricing])

  return (
    <DynamicQueryContent
      query={listingQuery}
    >
      <Fragment>
        {!!selectedSegment &&

          <Stack gap={2}>

            {productTypesToRender
              .map(({ productType, products }) => {
                const productsToRender = sortProductsKeepingSelectionGroupsAdjacent(
                  products.filter(product => (onlySelected && !!selectedProducts[product.id]) || !onlySelected)
                )


                if (!productsToRender.length) return null

                return (
                  <Fragment key={productType}>

                    {/* PRODUCT TITLE */}
                    {displayTypeHeading &&
                      <ProductListHeader
                        title={t(`product_type:${productType}`)}
                      />
                    }

                    {/* PRODUCT LIST */}
                    <Stack gap={2}>
                      {productsToRender
                        .map((product) => {
                          const isInteger = !!selectedCategory && isIntegerProduct(selectedCategory, product.kind)
                          const hasOptions = Object.values(product.options ?? {}).length > 0
                          const isProductPreview = disabledSelection && onlySelected
                          const selectedQuantity = selectedProducts[product.id]?.quantity
                          const isMeasurementLine = product.kind === ProductKind.MEASUREMENT_ON_SITE
                            || (product.bundledProducts ?? []).some((bundled) => bundled.kind === ProductKind.MEASUREMENT_ON_SITE)

                          let floorPlanDisclaimer = ''

                          const has3D = Object.values(selectedProductOptions[product.id] ?? {})?.some((option) => option.kind === ProductKind.FLOOR_PLAN_EDITING_3D)

                          if (isProductPreview && product.type === ProductType.FLOOR_PLAN && !has3D) {
                            if (product.kind === ProductKind.MEASUREMENT_ON_SITE) floorPlanDisclaimer = 'step_validation.floor_plan_disclaimer_measurements'
                            else floorPlanDisclaimer = 'step_validation.floor_plan_disclaimer_shooting'
                          }

                          const handleNumberChange = (value: number, option: CatalogueOptionProduct) => {
                            const normalizedValue = Math.min(100, Math.max(0, value || 0))

                            if (normalizedValue === 0) unselectOptionProduct(product.id, option.id)
                            else selectOptionProduct(product.id, option, normalizedValue)

                            handleLogOptionSelection(normalizedValue, option.kind, product.kind)
                          }

                          const handleCheckboxChange = (checked: boolean, option: CatalogueOptionProduct) => {
                            if (checked) selectOptionProduct(product.id, option, 1)
                            else unselectOptionProduct(product.id, option.id)

                            handleLogOptionSelection(checked ? 1 : 0, option.kind, product.kind)
                          }

                          const handleQuantityChange = (quantity?: number) => {
                            if (disabledSelection) return
                            if (!quantity || quantity <= 0) unselectProduct(product.id)
                            else selectProduct(product, quantity)

                            handleLogProductSelection(quantity ?? 0, product.kind, productType, product.documentSelectionGroup)
                          }

                          const handleCheckCardChange = () => {
                            if (disabledSelection) return

                            const willBeUnchecked = selectedQuantity && selectedQuantity > 0

                            if (willBeUnchecked) unselectProduct(product.id)
                            else selectProduct(product, 1)

                            // For construction files, only one can be selected at a time
                            if (productType === ProductType.CONSTRUCTION_FILE) {
                              const selectedConstructionProducts = Object.values(selectedProducts ?? {}).filter(
                                ({ type }) => type === ProductType.CONSTRUCTION_FILE
                              )

                              if (selectedConstructionProducts.length > 0) {
                                const [{ id: productToUnselectId }] = selectedConstructionProducts
                                unselectProduct(productToUnselectId)
                              }
                            }

                            // Products of the same backend-computed selection group all resolve to one
                            // DocEstate document, so only one may be selected per property (ISSUE-2866).
                            // Only on selection — unchecking one member must not remove its sibling.
                            // Logged directly (not via the shared debounced logger, which would collapse
                            // this with the product_selected event fired in the same tick).
                            if (!willBeUnchecked) {
                              productIdsDisplacedBySelectionGroup(product, selectedProducts ?? {}).forEach((displacedId) => {
                                unselectProduct(displacedId)
                                logAnalyticsEvent('product_displaced_by_selection_group', {
                                  displacedProductId: displacedId,
                                  selectedProductId: product.id,
                                  documentSelectionGroup: product.documentSelectionGroup,
                                })
                              })
                            }

                            handleLogProductSelection(willBeUnchecked ? 0 : 1, product.kind, productType, product.documentSelectionGroup)
                          }

                          const showExpandableContent = !isProductPreview
                            ? hasOptions
                            : Object.values(selectedProductOptions[product.id] ?? {}).length > 0

                          const expandableContent = (
                            <ProductCardContent
                              product={product}
                              onlySelected={onlySelected}
                              segmentKey={selectedSegment}
                              typeOrKindKey={productType}
                              discount={catalogueDiscount}
                              currency={catalogueCurrency}
                              disabledSelection={disabledSelection}
                              onNumberInputChange={handleNumberChange}
                              onCheckboxChange={handleCheckboxChange}
                            />
                          )

                          const getExpandableContent = () => {
                            if (showExpandableContent) return expandableContent
                            return undefined
                          }

                          return (
                            <TransitionGroup component={null} key={`${selectedSegment}_${productType}_${product.id}`}>
                              <Collapse
                                in={true}
                                timeout={standardTimeoutConfig}
                              >
                                <Stack gap={1}>
                                  {!isInteger &&
                                    <MUICheckCard
                                      disabled={disabled}
                                      showSelectElement={!isProductPreview}
                                      onCheck={handleCheckCardChange}
                                      checked={selectedQuantity > 0}
                                      disabledSelection={disabledSelection}
                                      isRadio={productType === ProductType.CONSTRUCTION_FILE || !!product.documentSelectionGroup}
                                      expandableContent={getExpandableContent()}
                                    >
                                      {!!catalogueCurrency && !!selectedCategory &&
                                        <ProductCardHeader
                                          product={product}
                                          disabled={disabled}
                                          typeKey={productType}
                                          category={selectedCategory}
                                          currency={catalogueCurrency}
                                          displayPrice={!isProductPreview}
                                          quantity={isProductPreview ? selectedQuantity : undefined}
                                        />
                                      }

                                      {isSelectedRegionRestricted && product.id === KampfmittelauskunftProductId &&
                                        <SystemMessage
                                          message={t('step_product.region_warning')}
                                          variant="warning"
                                          sx={{ marginTop: 1 }}
                                        />
                                      }

                                      {product.documentSelectionGroup === DocumentSelectionGroup.BEWILLIGUNGSURKUNDE &&
                                        <Typography variant="text-sm" color={GRAY_700} component="div" sx={{ marginTop: 1 }}>
                                          {t('step_product.bewilligungsurkunde_included_extract')}
                                        </Typography>
                                      }

                                    </MUICheckCard>
                                  }

                                  {isInteger &&
                                    <MUIIntegerCard
                                      inputPosition="right"
                                      disabled={disabled}
                                      defaultQuantity={selectedQuantity}
                                      expandThreshold={isProductPreview ? Number.POSITIVE_INFINITY : 1}
                                      hideCounterInput={!isProductPreview}
                                      onQuantityChange={handleQuantityChange}
                                      expandableContent={getExpandableContent()}
                                    >
                                      {!!catalogueCurrency && !!selectedCategory &&
                                        <ProductCardHeader
                                          product={product}
                                          disabled={disabled}
                                          typeKey={productType}
                                          category={selectedCategory}
                                          currency={catalogueCurrency}
                                          displayPrice={isProductPreview}
                                          quantity={isProductPreview ? selectedQuantity : undefined}
                                        />
                                      }
                                    </MUIIntegerCard>
                                  }

                                  {/* Cost Calculation Notice as an info card matching the review-step
                                    * measurement cards (Mila's design, ISSUE-3041) — measurement only; the
                                    * shooting disclaimer keeps the banner */}
                                  {!!floorPlanDisclaimer && product.kind === ProductKind.MEASUREMENT_ON_SITE &&
                                    <MeasurementInfoCard
                                      icon={<WarningAmberOutlinedIcon sx={measurementInfoCardIconSx} />}
                                      title={t('step_validation.cost_calculation_notice_title')}
                                    >
                                      <Typography variant="text-sm" color={GRAY_900}>
                                        {t('step_validation.cost_calculation_notice_text')}
                                      </Typography>
                                    </MeasurementInfoCard>
                                  }

                                  {isProductPreview && isMeasurementLine &&
                                    <MeasurementReviewInfo />
                                  }

                                  {!!floorPlanDisclaimer && product.kind !== ProductKind.MEASUREMENT_ON_SITE &&
                                    <SystemMessage
                                      variant="warning"
                                      message={<Trans
                                        t={t}
                                        i18nKey={floorPlanDisclaimer}
                                        components={{
                                          list: <ul />,
                                          item: <li />,
                                          bold: <strong />,
                                        }}
                                      >
                                        &nbsp;
                                      </Trans>}
                                    />
                                  }

                                </Stack>
                              </Collapse>
                            </TransitionGroup>
                          )
                        })
                      }

                    </Stack>
                  </Fragment>
                )
              })
            }

            {selectedCategory === DocumentCategory.AUTHORITIES_DOCUMENTS && disabledSelection && onlySelected &&
              <SystemMessage
                variant="error"
                title={t('order:authorities_documents_fee_info.title')}
                message={t('order:authorities_documents_fee_info.message')}
              />
            }

          </Stack>
        }
      </Fragment>
    </DynamicQueryContent>
  )
}
